/**
 * SystemSection - Microgrid Design Tab Content
 *
 * Extracts the DER configuration and grid settings from ScenarioEditor Tab 3.
 * Contains:
 * - Grid Constraints (import/export limits) - Vortx Flux only
 * - DER Configuration (Solar, Battery, Generator)
 * - Resilience settings - Vortx Flux only
 */

import React from 'react';
import { Box, TextField, Typography, Alert } from '@mui/material';
import { Build, ElectricBolt, Shield } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { SectionCard, vortxColors } from '@vortx/shared-ui';
import {
    SolarConfiguration,
    BatteryConfiguration,
    GeneratorConfiguration,
} from '../../../components/scenario';
import FluxPhdProfileSelector from '../../../components/scenario/FluxPhdProfileSelector';

/**
 * Grid Constraints Card
 * Allows limiting max power imported from or exported to the grid.
 */
function GridConstraintsCard({ vortxSettings, setVortxSettings }) {
    return (
        <SectionCard title="Grid Constraints" icon={<ElectricBolt sx={{ color: vortxColors.gold }} />}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Limit the maximum power that can be imported from or exported to the grid.
                Leave blank for no limit.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <TextField
                    label="Grid Import Limit (kW)"
                    type="number"
                    value={vortxSettings.gridImportLimitKw || ''}
                    onChange={(e) => setVortxSettings(prev => ({ ...prev, gridImportLimitKw: e.target.value }))}
                    placeholder="No limit"
                    helperText="Max power from grid"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        width: 200,
                        '& .MuiInputBase-input': { color: 'text.primary' },
                        '& .MuiInputLabel-root': { color: 'text.secondary' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: vortxColors.gold + '50' },
                        '& .MuiFormHelperText-root': { color: 'text.secondary' },
                    }}
                />
                <TextField
                    label="Grid Export Limit (kW)"
                    type="number"
                    value={vortxSettings.gridExportLimitKw || ''}
                    onChange={(e) => setVortxSettings(prev => ({ ...prev, gridExportLimitKw: e.target.value }))}
                    placeholder="No limit"
                    helperText="Max power to grid"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        width: 200,
                        '& .MuiInputBase-input': { color: 'text.primary' },
                        '& .MuiInputLabel-root': { color: 'text.secondary' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: vortxColors.gold + '50' },
                        '& .MuiFormHelperText-root': { color: 'text.secondary' },
                    }}
                />
            </Box>
        </SectionCard>
    );
}

/**
 * Resilience Card
 * Configures battery sizing for grid outage survival.
 */
function ResilienceCard({ vortxSettings, setVortxSettings, vortxStyles }) {
    return (
        <SectionCard title="Resilience" icon={<Shield sx={{ color: vortxColors.gold }} />}>
            <Alert severity="info" sx={{ mb: 2, borderColor: vortxColors.gold, color: vortxColors.charcoal }}>
                Sizes the battery to survive grid outages for critical loads.
            </Alert>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <TextField
                        label="Min Outage Survival (hours)"
                        type="number"
                        value={vortxSettings?.minOutageSurvivalHours || ''}
                        onChange={(e) => setVortxSettings(prev => ({ ...prev, minOutageSurvivalHours: e.target.value }))}
                        placeholder="e.g., 4"
                        helperText="Leave blank to skip resilience sizing"
                        fullWidth
                        sx={vortxStyles.textField}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <TextField
                        label="Critical Load Fraction"
                        type="number"
                        value={vortxSettings?.criticalLoadFraction || ''}
                        onChange={(e) => setVortxSettings(prev => ({ ...prev, criticalLoadFraction: e.target.value }))}
                        placeholder="0.5"
                        helperText="50% = half of load is critical"
                        fullWidth
                        sx={vortxStyles.textField}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
            </Box>
        </SectionCard>
    );
}

/**
 * DER Configuration Card
 * Contains Solar, Battery, Flux PhD Profile, and Generator configuration.
 */
function DERConfigurationCard({
    useStorage,
    useGenLoadBank,
    setUseGenLoadBank,
    optimizationEngine,
    vortxSettings,
    setVortxSettings,
    vortxStyles,
    onBatterySelect,
}) {
    return (
        <SectionCard title="DER Configuration" icon={<Build color="primary" />}>
            {/* Solar - Uses React Hook Form context */}
            <SolarConfiguration vortxStyles={vortxStyles} />

            {/* Battery - Uses React Hook Form context */}
            <BatteryConfiguration
                useGenLoadBank={useGenLoadBank}
                setUseGenLoadBank={setUseGenLoadBank}
                optimizationEngine={optimizationEngine}
                vortxSettings={vortxSettings}
                setVortxSettings={setVortxSettings}
                vortxStyles={vortxStyles}
                onBatterySelect={onBatterySelect}
            />

            {/* Flux PhD Efficiency Profile - only show when using storage */}
            {useStorage && optimizationEngine === 'vortx_flux' && (
                <FluxPhdProfileSelector
                    value={vortxSettings?.fluxPhdProfileId}
                    onChange={(profileId) => setVortxSettings(prev => ({
                        ...prev,
                        fluxPhdProfileId: profileId
                    }))}
                />
            )}

            {/* Generator - Uses React Hook Form context */}
            <GeneratorConfiguration vortxStyles={vortxStyles} />
        </SectionCard>
    );
}

/**
 * SystemSection Component
 *
 * Main component for Tab 3 (Microgrid Design) content.
 *
 * @param {Object} props
 * @param {string} props.optimizationEngine - Current optimization engine ('vortx_flux' or other)
 * @param {Object} props.vortxSettings - Vortx-specific settings state
 * @param {Function} props.setVortxSettings - Setter for vortx settings
 * @param {Object} props.vortxStyles - Styling object for consistent look
 * @param {boolean} props.useGenLoadBank - Generator load bank setting
 * @param {Function} props.setUseGenLoadBank - Setter for gen load bank
 * @param {Function} props.onBatterySelect - Callback when battery is selected from database
 */
function SystemSection({
    optimizationEngine,
    vortxSettings,
    setVortxSettings,
    vortxStyles,
    useGenLoadBank,
    setUseGenLoadBank,
    onBatterySelect,
}) {
    // Get useStorage from RHF context
    let useStorage = false;
    try {
        const formContext = useFormContext();
        useStorage = formContext?.watch('battery.enabled') || false;
    } catch (e) {
        // Not in form context, use false
    }

    const isVortxFlux = optimizationEngine === 'vortx_flux';

    return (
        <>
            {/* Grid Constraints - Only show when Vortx Flux solver is selected */}
            {isVortxFlux && (
                <GridConstraintsCard
                    vortxSettings={vortxSettings}
                    setVortxSettings={setVortxSettings}
                />
            )}

            <DERConfigurationCard
                useStorage={useStorage}
                useGenLoadBank={useGenLoadBank}
                setUseGenLoadBank={setUseGenLoadBank}
                optimizationEngine={optimizationEngine}
                vortxSettings={vortxSettings}
                setVortxSettings={setVortxSettings}
                vortxStyles={vortxStyles}
                onBatterySelect={onBatterySelect}
            />

            {/* Resilience Card - Only show when Vortx Flux solver is selected */}
            {isVortxFlux && (
                <ResilienceCard
                    vortxSettings={vortxSettings}
                    setVortxSettings={setVortxSettings}
                    vortxStyles={vortxStyles}
                />
            )}
        </>
    );
}

export default SystemSection;
